import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Chip, Avatar, Tooltip, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, TextField
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
  Add as AddIcon,
  PersonAdd as CheckInIcon,
  PersonRemove as CheckOutIcon,
  Event as EventIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import { GradientButton } from '../common/ModernButton';
import axiosInstance from '../axiosConfig';

const ScheduleCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week
  const [calendarData, setCalendarData] = useState({ shifts: [], time_off: [] });
  const [employees, setEmployees] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Form for assigning shift
  const [shiftForm, setShiftForm] = useState({
    employee_id: '',
    shift_type_id: '',
    shift_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchCalendarData();
    fetchEmployees();
    fetchShiftTypes();
  }, [currentDate, selectedEmployee]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const startDate = getMonthStart(currentDate);
      const endDate = getMonthEnd(currentDate);
      
      const params = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      };
      
      if (selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }

      const response = await axiosInstance.get('/api/schedule/calendar', { params });
      setCalendarData(response.data.data);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get('/api/employees');
      setEmployees(response.data.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchShiftTypes = async () => {
    try {
      // Asumiendo que hay un endpoint para obtener shift types
      const response = await axiosInstance.get('/api/schedule/shift-types');
      setShiftTypes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching shift types:', error);
      // Fallback a tipos predefinidos
      setShiftTypes([
        { id: 1, name: 'Morning Shift', color: '#4CAF50', start_time: '07:00', end_time: '15:00' },
        { id: 2, name: 'Afternoon Shift', color: '#FF9800', start_time: '15:00', end_time: '23:00' },
        { id: 3, name: 'Night Shift', color: '#2196F3', start_time: '23:00', end_time: '07:00' },
        { id: 4, name: 'Full Day', color: '#9C27B0', start_time: '09:00', end_time: '18:00' }
      ]);
    }
  };

  const handleAssignShift = async () => {
    try {
      await axiosInstance.post('/api/schedule/shifts', shiftForm);
      setShowAssignDialog(false);
      setShiftForm({ employee_id: '', shift_type_id: '', shift_date: '', notes: '' });
      fetchCalendarData();
    } catch (error) {
      console.error('Error assigning shift:', error);
      alert(error.response?.data?.error || 'Error al asignar turno');
    }
  };

  const getMonthStart = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getMonthEnd = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Días del mes anterior para completar la primera semana
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const day = new Date(year, month, -i);
      days.push({ date: day, isCurrentMonth: false });
    }
    
    // Días del mes actual
    for (let i = 1; i <= daysInMonth; i++) {
      const day = new Date(year, month, i);
      days.push({ date: day, isCurrentMonth: true });
    }
    
    // Días del siguiente mes para completar la última semana
    const remainingDays = 42 - days.length; // 6 semanas completas
    for (let i = 1; i <= remainingDays; i++) {
      const day = new Date(year, month + 1, i);
      days.push({ date: day, isCurrentMonth: false });
    }
    
    return days;
  };

  const getShiftsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return calendarData.shifts.filter(shift => shift.shift_date === dateStr);
  };

  const getTimeOffForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return calendarData.time_off.filter(timeOff => {
      return dateStr >= timeOff.start_date && dateStr <= timeOff.end_date;
    });
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const days = getDaysInMonth(currentDate);

  return (
    <ModernLayout title="Calendario de Empleados">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
            </Typography>
            <Box>
              <IconButton onClick={goToPreviousMonth} size="small">
                <PrevIcon />
              </IconButton>
              <IconButton onClick={goToToday} size="small">
                <TodayIcon />
              </IconButton>
              <IconButton onClick={goToNextMonth} size="small">
                <NextIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filtrar por empleado</InputLabel>
              <Select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                label="Filtrar por empleado"
              >
                <MenuItem value="all">Todos los empleados</MenuItem>
                {employees.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <GradientButton
              startIcon={<AddIcon />}
              onClick={() => setShowAssignDialog(true)}
            >
              Asignar Turno
            </GradientButton>
          </Box>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {shiftTypes.map(type => (
            <Chip
              key={type.id}
              label={type.name}
              size="small"
              sx={{
                bgcolor: type.color,
                color: 'white'
              }}
            />
          ))}
          <Chip label="Vacaciones" size="small" color="error" />
        </Box>

        {/* Calendar Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card>
            <CardContent sx={{ p: 0 }}>
              {/* Week day headers */}
              <Grid container sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {weekDays.map(day => (
                  <Grid
                    key={day}
                    item
                    xs={12 / 7}
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      fontWeight: 600,
                      bgcolor: 'grey.100'
                    }}
                  >
                    {day}
                  </Grid>
                ))}
              </Grid>

              {/* Calendar days */}
              <Grid container>
                {days.map((day, index) => {
                  const shifts = getShiftsForDate(day.date);
                  const timeOffs = getTimeOffForDate(day.date);
                  
                  return (
                    <Grid
                      key={index}
                      item
                      xs={12 / 7}
                      sx={{
                        minHeight: 120,
                        border: 1,
                        borderColor: 'divider',
                        p: 1,
                        bgcolor: day.isCurrentMonth ? 'background.paper' : 'grey.50',
                        position: 'relative',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: day.isCurrentMonth ? 'action.hover' : 'grey.100'
                        }
                      }}
                      onClick={() => {
                        setSelectedDate(day.date);
                        setShiftForm({
                          ...shiftForm,
                          shift_date: day.date.toISOString().split('T')[0]
                        });
                      }}
                    >
                      {/* Date number */}
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: isToday(day.date) ? 'primary.main' : 'transparent',
                          color: isToday(day.date) ? 'white' : 'text.primary',
                          fontWeight: isToday(day.date) ? 700 : 400,
                          fontSize: '0.875rem',
                          mb: 0.5
                        }}
                      >
                        {day.date.getDate()}
                      </Box>

                      {/* Shifts for this day */}
                      <Box sx={{ mt: 0.5 }}>
                        {shifts.slice(0, 3).map(shift => {
                          const shiftType = shiftTypes.find(st => st.name === shift.shift_name);
                          return (
                            <Tooltip
                              key={shift.id}
                              title={`${shift.employee_name} - ${shift.shift_name} (${shift.start_time} - ${shift.end_time})`}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  p: 0.5,
                                  mb: 0.5,
                                  borderRadius: 1,
                                  bgcolor: shiftType?.color || '#9E9E9E',
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <Avatar
                                  sx={{ width: 16, height: 16, fontSize: '0.6rem' }}
                                >
                                  {shift.employee_name[0]}
                                </Avatar>
                                <Typography variant="caption" noWrap>
                                  {shift.employee_name.split(' ')[0]}
                                </Typography>
                                {shift.status === 'checked_in' && (
                                  <CheckInIcon sx={{ fontSize: 12 }} />
                                )}
                              </Box>
                            </Tooltip>
                          );
                        })}
                        
                        {shifts.length > 3 && (
                          <Typography variant="caption" color="text.secondary">
                            +{shifts.length - 3} más
                          </Typography>
                        )}

                        {/* Time off */}
                        {timeOffs.map(timeOff => (
                          <Tooltip
                            key={timeOff.id}
                            title={`${timeOff.employee_name} - ${timeOff.request_type}`}
                          >
                            <Box
                              sx={{
                                p: 0.5,
                                mb: 0.5,
                                borderRadius: 1,
                                bgcolor: 'error.main',
                                color: 'white',
                                fontSize: '0.7rem',
                                textAlign: 'center'
                              }}
                            >
                              🏖️ {timeOff.employee_name.split(' ')[0]}
                            </Box>
                          </Tooltip>
                        ))}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Assign Shift Dialog */}
      <Dialog
        open={showAssignDialog}
        onClose={() => setShowAssignDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Asignar Turno</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Empleado</InputLabel>
              <Select
                value={shiftForm.employee_id}
                onChange={(e) => setShiftForm({ ...shiftForm, employee_id: e.target.value })}
                label="Empleado"
                required
              >
                {employees.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.name} - {emp.role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Tipo de Turno</InputLabel>
              <Select
                value={shiftForm.shift_type_id}
                onChange={(e) => setShiftForm({ ...shiftForm, shift_type_id: e.target.value })}
                label="Tipo de Turno"
                required
              >
                {shiftTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: type.color
                        }}
                      />
                      {type.name} ({type.start_time} - {type.end_time})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="date"
              label="Fecha"
              value={shiftForm.shift_date}
              onChange={(e) => setShiftForm({ ...shiftForm, shift_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              label="Notas (opcional)"
              multiline
              rows={2}
              value={shiftForm.notes}
              onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAssignDialog(false)}>Cancelar</Button>
          <GradientButton
            onClick={handleAssignShift}
            disabled={!shiftForm.employee_id || !shiftForm.shift_type_id || !shiftForm.shift_date}
          >
            Asignar Turno
          </GradientButton>
        </DialogActions>
      </Dialog>
    </ModernLayout>
  );
};

export default ScheduleCalendar;

